import React, { useState } from 'react'
import { isNull } from 'lodash'
import {
  SortDirectionEnum,
  Events_events_pagination,
  Events_events_events,
  SortByType,
  getFormattedDateStartAndEndTime,
  EventFrequency,
  getFormattedStartAndEndTimeInMinutes,
  useAuth,
  UserPermissions,
  useDeleteEvent,
} from '@modmd/data'
import { COLOR, DEVICE, fontSizes } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import Card from 'components/Card'
import { Box } from 'components/Layout'
import { isBefore, hoursToMinutes } from 'date-fns'
import Button from 'components/Button'
import { Text } from 'components/Typography'
import { useHistory } from 'react-router'
import Icon from 'components/Icon'
import { DeleteEventDialog } from 'internal-portal/routes/EventsList/DeleteEventDialog'
import { useToaster } from 'utils/useToaster'
import { Spinner } from 'components/Spinner'
import { EventPaymentLabel } from 'sharedComponents/EventPaymentLabel'

interface ColumnLabels {
  eventId?: string | null
  frequency?: string | null
  date?: string | null
  address?: string | null
  name?: string | null
  status?: string | null
  companyId?: string | null
  groupId?: string | null
  actions?: string | null
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  events: Events_events_events[]
  pagination?: Events_events_pagination
  sorter?: DataListProps['sorter']
  requestDebounceInMs?: number
  isLoading: boolean
  pageLength?: number
  columnLabels?: ColumnLabels
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  onPageChange?: (page: number) => void
  onRowClick?: (data: Events_events_events) => void
  openEditEvent: (eventId: string) => void
  refetchEvents: () => Promise<unknown>
}

const defaultColumnLabels = {
  eventId: 'Event ID',
  frequency: 'Frequency',
  date: 'Date',
  address: 'Location',
  name: 'Name',
  numPeople: 'Number of People',
  status: 'Status',
  group: 'Group',
  actions: 'Actions',
  paymentMethod: 'Payment Method',
  sublocation: 'Rate type',
}

const PAGE_LENGTH = 10

export const EventDatalist: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  events,
  sorter,
  pagination,
  isLoading,
  columnLabels: providedColumnLabels,
  onSortChange,
  onPageChange,
  pageLength = PAGE_LENGTH,
  openEditEvent,
  refetchEvents,
}) => {
  const { hasPermission } = useAuth()
  const history = useHistory()
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = React.useMemo(() => `repeat(${numOfColumns - 1}, 1fr) 1.5fr`, [
    numOfColumns,
  ])
  const [deleteEventId, setDeleteEventId] = useState<string | undefined>(undefined)
  const { setToastMessage } = useToaster()

  const [deleteEvent, { loading: isDeletingEvent }] = useDeleteEvent({
    onCompleted: async () => {
      setToastMessage('Event deleted successfully', 'success')
      setDeleteEventId(undefined)
      await refetchEvents()
    },
  })

  const getEventStatus = (isActive: boolean, finishedAt: Date | null, firstDay: Date | null) => {
    if (finishedAt) return 'Finished'
    if (!isActive) return 'Inactive'
    if (firstDay && isBefore(new Date(), firstDay)) return 'Waiting'
    return 'Active'
  }

  const canEditEvent = React.useCallback(
    (isFinished: boolean) =>
      (hasPermission([UserPermissions.EDIT_EVENT]) && !isFinished) ||
      hasPermission([UserPermissions.EDIT_CLOSED_EVENTS]),
    [hasPermission]
  )

  interface ActionButtonProps {
    label: string
    action?: () => void
    url?: string
    isDisabled?: boolean
  }

  const ActionButton: React.VFC<ActionButtonProps> = ({ label, isDisabled, action, url }) => (
    <Button href={url} appearance="link" onClick={action} disabled={isDisabled} colorVariant="menu">
      <Text fontSize={fontSizes.s}>{label}</Text>
    </Button>
  )

  return (
    <>
      <Card>
        <Datalist
          header={tableHeader}
          isLoading={isLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={events}
          sorter={sorter}
          onSortChange={onSortChange}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={() => (
            <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
              {!isNull(columnLabels.eventId) ? (
                <Datalist.HeaderCell label={columnLabels.eventId} />
              ) : null}
              {!isNull(columnLabels.frequency) ? (
                <Datalist.HeaderCell label={columnLabels.frequency} />
              ) : null}
              {!isNull(columnLabels.date) ? (
                <Datalist.HeaderCell sortKey={SortByType.EVENT_DAY} label={columnLabels.date} />
              ) : null}
              {!isNull(columnLabels.address) ? (
                <Datalist.HeaderCell label={columnLabels.address} />
              ) : null}
              {!isNull(columnLabels.sublocation) ? (
                <Datalist.HeaderCell label={columnLabels.sublocation} />
              ) : null}
              {!isNull(columnLabels.name) ? (
                <Datalist.HeaderCell label={columnLabels.name} />
              ) : null}
              {!isNull(columnLabels.numPeople) ? (
                <Datalist.HeaderCell label={columnLabels.numPeople} />
              ) : null}
              {!isNull(columnLabels.status) ? (
                <Datalist.HeaderCell label={columnLabels.status} />
              ) : null}
              {!isNull(columnLabels.group) ? (
                <Datalist.HeaderCell label={columnLabels.group} />
              ) : null}
              {!isNull(columnLabels.paymentMethod) ? (
                <Datalist.HeaderCell
                  sortKey={SortByType.PAYMENT_METHOD}
                  label={columnLabels.paymentMethod}
                />
              ) : null}
              {!isNull(columnLabels.actions) ? (
                <Datalist.HeaderCell label={columnLabels.actions} />
              ) : null}
            </Datalist.Row>
          )}
          renderTableRow={(data) => {
            const {
              id,
              frequency,
              EventDays,
              Address,
              name,
              isActive,
              finishedAt,
              slug,
              Company,
              Group,
              countAttendee,
              paymentMethod,
              countSamples,
              Invoice,
              sublocation,
            } = data

            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: rowGridTemplateColumns,
                }}
                {...(!isDesktop ? { alignItems: 'start' } : {})}
                onClick={() => history.push(`/events/${id}`)}
              >
                {!isNull(columnLabels.eventId) ? (
                  <Datalist.Cell label={columnLabels.eventId}>
                    <Box>{id}</Box>
                    <Box>
                      <Text fontSize="s" color={COLOR.grayDark}>
                        {slug}
                      </Text>
                    </Box>
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.frequency) ? (
                  <Datalist.Cell label={columnLabels.frequency}>{frequency}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.date) ? (
                  <Datalist.Cell label={columnLabels.date}>
                    {!EventDays?.length && '-'}
                    {frequency === EventFrequency.Weekly
                      ? EventDays?.map((day) => (
                          <Box>
                            {getFormattedStartAndEndTimeInMinutes(
                              new Date(day.startTime),
                              hoursToMinutes(day.timeRange)
                            )}
                          </Box>
                        ))
                      : EventDays?.map((day) => (
                          <Box>
                            {getFormattedDateStartAndEndTime(
                              new Date(day.startTime),
                              day.timeRange
                            )}
                          </Box>
                        ))}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.address) ? (
                  <Datalist.Cell label={columnLabels.address}>
                    {!Address ? (
                      'No address'
                    ) : (
                      <>
                        {Address?.street}, {Address?.city}, {Address?.state}
                      </>
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.sublocation) ? (
                  <Datalist.Cell label={columnLabels.sublocation}>{sublocation}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.name) ? (
                  <Datalist.Cell label={columnLabels.name}>{name}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.numPeople) ? (
                  <Datalist.Cell label={columnLabels.numPeople}>{`${Number(countSamples) || '0'}/${
                    countAttendee || '0'
                  }`}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.status) ? (
                  <Datalist.Cell label={columnLabels.status}>
                    {getEventStatus(
                      isActive,
                      finishedAt ? new Date(finishedAt) : null,
                      EventDays && EventDays[0] ? new Date(EventDays[0].startTime) : null
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.groupId) ? (
                  <Datalist.Cell label={columnLabels.group}>
                    {Group?.name ? `${Group?.name || ''} (${Company?.name || ''})` : '-'}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.paymentMethod) ? (
                  <Datalist.Cell label={columnLabels.paymentMethod}>
                    <EventPaymentLabel
                      paymentStatus={paymentMethod}
                      invoiceDocNumber={Invoice?.invoiceDocNumber}
                    />
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.actions) ? (
                  <Datalist.Cell>
                    <Box display="grid" gridGap="0.1rem" gridTemplateColumns="1fr 0.3fr 0.2fr">
                      <Box>
                        <ActionButton
                          label="Client Link"
                          url={`${
                            process.env.REACT_APP_MOD_EVENT_URL as string
                          }/event/${id}/event-days`}
                        />
                        <ActionButton
                          label="Nurse Link"
                          url={`${process.env.REACT_APP_MOD_EVENT_URL as string}/testing/${id}`}
                        />
                      </Box>
                      {canEditEvent(!!finishedAt) && (
                        <Button
                          disabled={
                            !!finishedAt && !hasPermission([UserPermissions.EDIT_CLOSED_EVENTS])
                          }
                          onClick={(e) => {
                            e.stopPropagation()
                            openEditEvent(id)
                          }}
                          appearance="ghost"
                          colorVariant="menu"
                          size="small"
                        >
                          <Icon.Edit />
                        </Button>
                      )}

                      {hasPermission([UserPermissions.DELETE_EVENT]) &&
                        !countAttendee &&
                        !finishedAt && (
                          <Button
                            disabled={!!finishedAt || isDeletingEvent}
                            onClick={(e) => {
                              e.stopPropagation()
                              setDeleteEventId(id)
                            }}
                            appearance="ghost"
                            colorVariant="danger"
                            size="small"
                          >
                            {isDeletingEvent && deleteEventId === id ? (
                              <Spinner color={COLOR.danger} size="small" />
                            ) : (
                              <Icon.Trash />
                            )}
                          </Button>
                        )}
                    </Box>
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
          footer={tableFooter}
        />
      </Card>
      {pagination && pagination.total > pagination.length && onPageChange && (
        <Pagination.Wrapper>
          <Pagination
            firstPage={1}
            lastPage={Math.ceil(pagination.total / pagination.length) || 1}
            currentPage={Math.ceil(pagination.from / pagination.length) + 1}
            onPageChange={onPageChange}
          />
        </Pagination.Wrapper>
      )}
      <DeleteEventDialog
        isOpen={!!deleteEventId}
        onDismiss={() => setDeleteEventId(undefined)}
        onDelete={async () => {
          try {
            await deleteEvent({
              variables: {
                id: String(deleteEventId),
              },
            })
          } catch {
            // ignore
          }
        }}
      />
    </>
  )
}
